// src/pages/admin/AdminRawSkillsScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, Layers, Clock, GraduationCap, Target } from 'lucide-react';

const DEFAULTS = {
  grades: {
    grade1: { enabled: true,  weight: 20 },
    grade2: { enabled: true,  weight: 30 },
    grade3: { enabled: true,  weight: 30 },
    grade4: { enabled: false, weight: 15 },
    grade5: { enabled: false, weight: 5 },
  },
  durationBoost: {
    enabled: true,
    maxBoostPercent: 15,
    halfLifeMonths: 24,
  },
  educationBoost: {
    enabled: true,
    maxBoostPercent: 15,
    levelScores: {
      highSchool:           10,
      diploma:              25,
      advancedDiploma:      35,
      graduateCertificate:  45,
      graduateDiploma:      55,
      bachelor:             70,
      master:               85,
      doctorate:            100,
    },
  },
  motivationBoost: {
    enabled: true,
    requiredMultiplier:   100,
    preferredMultiplier:  70,
    unspecifiedMultiplier: 40,
  },
};

const GRADE_INFO = [
  { key: 'grade1', label: 'Grade 1 — Collated (All)',
    desc: 'All skills across all sections are collated for one seeker and compared to the collated skills for the position.' },
  { key: 'grade2', label: 'Grade 2 — Per Section',
    desc: 'Skills are collated per section (General, Soft, Software, Hardware, custom). Section name meaning is factored into the comparison.' },
  { key: 'grade3', label: 'Grade 3 — Per Section + Source Type',
    desc: 'Skills are collated per section and derivation source type (education, experience, manual). Section name and source type are factored into the comparison.' },
  { key: 'grade4', label: 'Grade 4 — Per Section + Source Item',
    desc: 'Skills are collated per section and specific derivation source (each education/experience item). Position name and field of study are factored into the comparison.' },
  { key: 'grade5', label: 'Grade 5 — Individual Skills',
    desc: 'Each seeker skill is compared individually to position skills. Section name, derivation source type, position name, and field of study are all factored into the comparison.' },
];

const EDUCATION_LEVELS = [
  { key: 'highSchool',          label: 'High School Certificate' },
  { key: 'diploma',             label: 'Diploma' },
  { key: 'advancedDiploma',     label: 'Advanced Diploma' },
  { key: 'graduateCertificate', label: 'Graduate Certificate' },
  { key: 'graduateDiploma',     label: 'Graduate Diploma' },
  { key: 'bachelor',            label: 'Bachelor Degree' },
  { key: 'master',              label: 'Master Degree' },
  { key: 'doctorate',           label: 'Doctorate' },
];

/* ── Toggle switch ───────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      style={{ opacity: 0, width: 0, height: 0 }} />
    <span style={{
      position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
      background: checked ? '#28A745' : '#ccc', transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', height: 18, width: 18, left: checked ? 20 : 4, top: 3,
        background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
      }} />
    </span>
  </label>
);

/* ── Grade row (toggle + weight) ─────────────────────────── */
const GradeRow = ({ info, grade, onChange }) => (
  <div style={{ padding: '14px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={grade.enabled} onChange={v => onChange({ enabled: v })} />
          <label className="form-label" style={{ marginBottom: 0 }}>{info.label}</label>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 54 }}>{info.desc}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: grade.enabled ? 1 : 0.4 }}>
        <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>Weight</span>
        <input
          type="number" min="0" max="100" value={grade.weight} disabled={!grade.enabled}
          onChange={e => onChange({ weight: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
          className="form-input"
          style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
        />
      </div>
    </div>
  </div>
);

/* ── Score row (label + input) ───────────────────────────── */
const ScoreRow = ({ label, value, onChange, suffix, description, max }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={max || 999} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max || 999, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888', minWidth: 40 }}>{suffix || ''}</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminRawSkillsScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    grades: {
      grade1: { ...DEFAULTS.grades.grade1, ...d.grades?.grade1 },
      grade2: { ...DEFAULTS.grades.grade2, ...d.grades?.grade2 },
      grade3: { ...DEFAULTS.grades.grade3, ...d.grades?.grade3 },
      grade4: { ...DEFAULTS.grades.grade4, ...d.grades?.grade4 },
      grade5: { ...DEFAULTS.grades.grade5, ...d.grades?.grade5 },
    },
    durationBoost: { ...DEFAULTS.durationBoost, ...d.durationBoost },
    educationBoost: {
      ...DEFAULTS.educationBoost,
      ...d.educationBoost,
      levelScores: { ...DEFAULTS.educationBoost.levelScores, ...d.educationBoost?.levelScores },
    },
    motivationBoost: { ...DEFAULTS.motivationBoost, ...d.motivationBoost },
  });

  useEffect(() => {
    matchingModelAPI.getRawSkillsConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateRawSkillsConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Raw skills scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateGrade = (key) => (patch) =>
    setConfig(c => ({ ...c, grades: { ...c.grades, [key]: { ...c.grades[key], ...patch } } }));

  const updateDuration = (patch) =>
    setConfig(c => ({ ...c, durationBoost: { ...c.durationBoost, ...patch } }));

  const updateEducation = (patch) =>
    setConfig(c => ({ ...c, educationBoost: { ...c.educationBoost, ...patch } }));

  const updateLevelScore = (key, value) =>
    setConfig(c => ({ ...c, educationBoost: { ...c.educationBoost, levelScores: { ...c.educationBoost.levelScores, [key]: value } } }));

  const updateMotivation = (patch) =>
    setConfig(c => ({ ...c, motivationBoost: { ...c.motivationBoost, ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const enabledGrades = GRADE_INFO.filter(g => config.grades[g.key].enabled);
  const totalWeight = enabledGrades.reduce((sum, g) => sum + config.grades[g.key].weight, 0);

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure semantic matching between seeker profile skills and ad position expected candidate skills.
        Skills are compared across standard sections (General, Soft, Software, Hardware) and custom sections.
        Cosine similarity is applied at different granularity levels.
      </p>

      {/* ── Granularity Grades ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Layers size={18} />
          <span>Granularity Grades</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          {GRADE_INFO.map((info, i) => (
            <React.Fragment key={info.key}>
              {i > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
              <GradeRow info={info} grade={config.grades[info.key]} onChange={updateGrade(info.key)} />
            </React.Fragment>
          ))}

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {enabledGrades.length} of 5 grades enabled
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: totalWeight === 100 ? '#28A745' : '#CC0000' }}>
              Total weight: {totalWeight} / 100
            </span>
          </div>
          {totalWeight !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Weights of enabled grades should sum to 100. They will be normalised at scoring time.
            </div>
          )}
        </div>
      </div>

      {/* ── Seeker-side Boosts ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Seeker-Side Boosts
      </div>

      {/* ── Experience Duration Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={18} />
          <span>Experience Duration Boost</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Skills derived from longer work experience receive a stronger match score.
            Applies to experience-derived skills in Grades 3 and 4.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Toggle checked={config.durationBoost.enabled} onChange={v => updateDuration({ enabled: v })} />
            <label className="form-label" style={{ marginBottom: 0 }}>Enable Duration Boost</label>
          </div>

          <div style={{ opacity: config.durationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Max Boost" value={config.durationBoost.maxBoostPercent}
              onChange={v => updateDuration({ maxBoostPercent: v })} suffix="%" max={100}
              description="Maximum percentage increase to the similarity score from experience duration." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Half-Life" value={config.durationBoost.halfLifeMonths}
              onChange={v => updateDuration({ halfLifeMonths: v })} suffix="months" max={999}
              description="Months at which the boost reaches half its maximum value." />
          </div>
        </div>
      </div>

      {/* ── Education Level Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GraduationCap size={18} />
          <span>Education Level Boost</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Skills derived from higher qualifications receive a stronger match score.
            Applies to education-derived skills in Grades 3 and 4.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Toggle checked={config.educationBoost.enabled} onChange={v => updateEducation({ enabled: v })} />
            <label className="form-label" style={{ marginBottom: 0 }}>Enable Education Boost</label>
          </div>

          <div style={{ opacity: config.educationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Max Boost" value={config.educationBoost.maxBoostPercent}
              onChange={v => updateEducation({ maxBoostPercent: v })} suffix="%" max={100}
              description="Maximum percentage increase to the similarity score from qualification level." />
            <div style={{ borderTop: '1px solid #eee', marginBottom: 4 }} />

            <div style={{ padding: '6px 0 2px' }}>
              <label className="form-label" style={{ marginBottom: 4 }}>Level Scores</label>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                Relative strength of each qualification level (0–100). Higher values produce a stronger boost.
              </div>
            </div>
            {EDUCATION_LEVELS.map((lvl, i) => (
              <React.Fragment key={lvl.key}>
                {i > 0 && <div style={{ borderTop: '1px solid #f5f5f5' }} />}
                <ScoreRow label={lvl.label} value={config.educationBoost.levelScores[lvl.key]}
                  onChange={v => updateLevelScore(lvl.key, Math.min(100, v))} suffix="/ 100" max={100} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── Position-side Boosts ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Position-Side Boosts
      </div>

      {/* ── Motivation Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Target size={18} />
          <span>Recruitment Motivation</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Skills marked as Required by the employer carry more weight than Preferred or Unspecified skills.
            These multipliers scale the contribution of each matched skill.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Toggle checked={config.motivationBoost.enabled} onChange={v => updateMotivation({ enabled: v })} />
            <label className="form-label" style={{ marginBottom: 0 }}>Enable Motivation Weighting</label>
          </div>

          <div style={{ opacity: config.motivationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Required" value={config.motivationBoost.requiredMultiplier}
              onChange={v => updateMotivation({ requiredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for skills marked as Required by the employer." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Preferred" value={config.motivationBoost.preferredMultiplier}
              onChange={v => updateMotivation({ preferredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for skills marked as Preferred by the employer." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Unspecified" value={config.motivationBoost.unspecifiedMultiplier}
              onChange={v => updateMotivation({ unspecifiedMultiplier: v })} suffix="%" max={200}
              description="Multiplier for skills with no motivation specified." />
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
